'use client';

import { ArrowUp, ChevronDown, Loader2, Mic } from 'lucide-react';

import { cn } from '../../utils/cn';
import { Button } from '../button';
import { ButtonGroup, ButtonGroupItem } from '../button-group';
import { Tooltip } from '../tooltip';

export type ComposerSendMode =
	/** Mic (empty compact state) — subtle white background */
	| 'mic'
	/** Single arrow-up, white bg — the only send affordance the composer uses */
	| 'plain'
	/** Split: arrow-up + chevron for a caller-supplied menu of send targets */
	| 'split';

interface ComposerSendButtonProps {
	mode: ComposerSendMode;
	onSend: () => void;
	onOpenMenu?: () => void;
	disabled?: boolean;
	/** When true the primary pill uses the private (orange) theme instead of neutral. */
	privateAccent?: boolean;
	/** Mic mode only: the welcome hero treatment — 48px seat with the send
	 *  button's strong fill and radius, so typing morphs it into the send button. */
	heroMic?: boolean;
	/** Render a spinner in place of the primary icon and force-disable interaction. */
	loading?: boolean;
	className?: string;
}

export const ComposerSendButton = ({
	mode,
	onSend,
	onOpenMenu,
	disabled,
	privateAccent = false,
	heroMic = false,
	loading = false,
	className,
}: ComposerSendButtonProps) => {
	const theme = privateAccent ? 'private' : 'neutral';
	const isDisabled = disabled || loading;

	// The mic ⇄ send flip swaps classes on the SAME element (both branches render
	// Tooltip > Button), so transitioning the box lets the hero mic's 48px seat
	// tween down into the 32px send seat instead of snapping. Back-out curve —
	// overshoots a touch, in lockstep with COMPOSER_BOUNCE on the seat wrapper.
	const sizeMorphClass =
		'transition-[width,height,border-radius] duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]';

	if (mode === 'mic') {
		return (
			<Tooltip content={loading ? 'Sending…' : 'Record voice message'}>
				<Button
					variant={heroMic ? 'solid-strong-theme' : 'solid-muted'}
					theme={theme}
					size={heroMic ? 'fab' : 'sm'}
					iconOnly
					aria-label={loading ? 'Sending' : 'Start voice recording'}
					onClick={onSend}
					disabled={isDisabled}
					className={cn(
						sizeMorphClass,
						// Concentric corner: the hero seat sits 8px inside the shell, so its
						// radius is the shell's minus the gap — floored at the send button's
						// 12px so it never reads squarer than the button it morphs into.
						heroMic && 'rounded-[max(12px,calc(var(--composer-radius,16px)_-_8px))]',
						className,
					)}
				>
					{loading ? <Loader2 className="animate-spin" strokeWidth={2} /> : <Mic strokeWidth={2} />}
				</Button>
			</Tooltip>
		);
	}

	if (mode === 'plain') {
		return (
			<Tooltip content={loading ? 'Sending…' : 'Send'}>
				<Button
					variant="solid-strong-theme"
					theme={theme}
					size="sm"
					iconOnly
					aria-label={loading ? 'Sending' : 'Send message'}
					onClick={onSend}
					disabled={isDisabled}
					className={cn(sizeMorphClass, className)}
				>
					{loading ? <Loader2 className="animate-spin" strokeWidth={2} /> : <ArrowUp strokeWidth={2} />}
				</Button>
			</Tooltip>
		);
	}

	// mode === 'split'
	return (
		<ButtonGroup variant="solid-strong" theme={theme} className={className}>
			<Tooltip content={loading ? 'Sending…' : 'Send'}>
				<ButtonGroupItem
					iconOnly
					onClick={onSend}
					disabled={isDisabled}
					aria-label={loading ? 'Sending' : 'Send message'}
				>
					{loading ? <Loader2 className="animate-spin" strokeWidth={2} /> : <ArrowUp strokeWidth={2} />}
				</ButtonGroupItem>
			</Tooltip>
			<Tooltip content="More options">
				<ButtonGroupItem
					chevron
					onClick={onOpenMenu}
					disabled={isDisabled}
					aria-label="More send options"
				>
					<ChevronDown strokeWidth={2} />
				</ButtonGroupItem>
			</Tooltip>
		</ButtonGroup>
	);
};
